/**
 * 全息量子引擎 — 完整叙事动画
 * Holographic Quantum Engine — Full Narrative Animation
 *
 * 五幕结构（按 durationInFrames 自适应比例）：
 *   1. 觉醒  0%–20%   系统初始化，粒子从虚无中诞生
 *   2. 成形 20%–40%   粒子聚合为全息球体，神经连接建立
 *   3. 赋能 40%–67%   展示三大核心能力卡片
 *   4. 超越 67%–87%   色彩蜕变为金色，重新定义交互
 *   5. 发布 87%–100%  品牌亮相 HOLOGRAPHIC · 2026
 */

import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  random,
} from 'remotion';

// ============ 配置 ============
const CONFIG = {
  particleCount: 220,
  sphereRadius: 260,
  minSize: 1.5,
  maxSize: 6,
  rotationSpeed: 0.4,
  oscillationSpeed: 1.8,
  connectionDist: 80,
  maxConnections: 120,
  scanlineGap: 3,
  scanlineAlpha: 0.06,
};

const FEATURES = [
  {
    icon: '◈',
    title: '实时全息投影',
    stat: '< 1ms',
    label: '响应延迟',
    pct: 0.03,
    color: '#00d4ff',
  },
  {
    icon: '⚡',
    title: '量子并行运算',
    stat: '10¹⁸',
    label: '次 / 秒',
    pct: 0.12,
    color: '#a855f7',
  },
  {
    icon: '◉',
    title: '自适应智能学习',
    stat: '99.97%',
    label: '准确率',
    pct: 0.21,
    color: '#10b981',
  },
] as const;

// ============ 粒子 ============
interface Particle {
  theta: number;
  phi: number;
  size: number;
  speed: number;
  phase: number;
  initialTheta: number;
  initialPhi: number;
  birthPct: number;
}

const generateParticles = (count: number): Particle[] => {
  const golden = (1 + Math.sqrt(5)) / 2;
  return Array.from({ length: count }, (_, i) => {
    const theta = (2 * Math.PI * i) / golden;
    const phi = Math.acos(1 - (2 * (i + 0.5)) / count);
    return {
      theta,
      phi,
      size: interpolate(
        random(`s-${i}`),
        [0, 1],
        [CONFIG.minSize, CONFIG.maxSize],
      ),
      speed: interpolate(random(`v-${i}`), [0, 1], [0.7, 1.3]),
      phase: random(`p-${i}`) * Math.PI * 2,
      initialTheta: theta,
      initialPhi: phi,
      birthPct: interpolate(random(`b-${i}`), [0, 1], [0, 0.16]),
    };
  });
};

const toCartesian = (theta: number, phi: number, r: number) => ({
  x: r * Math.sin(phi) * Math.cos(theta),
  y: r * Math.sin(phi) * Math.sin(theta),
  z: r * Math.cos(phi),
});

const ci = (
  v: number,
  inp: [number, number],
  out: [number, number],
) =>
  interpolate(v, inp, out, {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });

const ci3 = (
  v: number,
  inp: [number, number, number],
  out: [number, number, number],
) =>
  interpolate(v, inp, out, {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });

// ============ 主组件 ============
export const HolographicParticles: React.FC = () => {
  const frame = useCurrentFrame();
  const { width, height, fps, durationInFrames: D } = useVideoConfig();
  const particles = React.useMemo(
    () => generateParticles(CONFIG.particleCount),
    [],
  );

  const progress = frame / fps;
  const cx = width / 2;
  const cy = height / 2;

  // 阶段帧号（按百分比自适应）
  const P = {
    awk: { s: 0, e: D * 0.2 },
    frm: { s: D * 0.2, e: D * 0.4 },
    cap: { s: D * 0.4, e: D * 0.667 },
    trs: { s: D * 0.667, e: D * 0.867 },
    lch: { s: D * 0.867, e: D },
  };

  // 阶段进度 0→1
  const awkP = ci(frame, [P.awk.s, P.awk.e], [0, 1]);
  const frmP = ci(frame, [P.frm.s, P.frm.e], [0, 1]);
  const trsP = ci(frame, [P.trs.s, P.trs.e], [0, 1]);
  const lchP = ci(frame, [P.lch.s, P.lch.e], [0, 1]);

  // 全局色调：启动→成形→能力阶段保持青蓝色，超越阶段渐变为金色
  const baseHue =
    frame < P.trs.s ? 200 : interpolate(trsP, [0, 1], [200, 45]);

  // 球体半径：超越时呼吸
  const radius =
    CONFIG.sphereRadius *
    (frame < P.trs.s
      ? 1
      : 1 + Math.sin(trsP * Math.PI) * 0.15);

  // 中心核心发光强度
  const coreGlow =
    frame < D * 0.03
      ? ci(frame, [0, D * 0.03], [0, 0.8])
      : frame < P.lch.s
        ? 0.3 + Math.sin(progress * 3) * 0.2
        : ci3(
            frame,
            [P.lch.s, P.lch.s + D * 0.02, P.lch.s + D * 0.06],
            [0.3, 1, 0.5],
          );

  // 粒子位置
  const particleData = particles.map((p, i) => {
    const birthFrame = p.birthPct * D;
    const birthF = ci(frame, [birthFrame, birthFrame + D * 0.04], [0, 1]);
    const theta =
      p.initialTheta + progress * CONFIG.rotationSpeed * p.speed;
    const osc =
      Math.sin(progress * CONFIG.oscillationSpeed + p.phase) * 0.08;
    const phi = p.initialPhi + osc;
    const pos = toCartesian(theta, phi, radius);
    const depth = (pos.z + radius) / (2 * radius);
    const sizeK = interpolate(depth, [0, 1], [0.5, 1.5]);
    const opacity = interpolate(depth, [0, 1], [0.2, 0.9]) * birthF;

    let hue: number;
    if (frame < P.trs.s) {
      hue = ((theta / (2 * Math.PI)) * 360 + frame * 0.5) % 360;
    } else {
      const rainbow =
        ((theta / (2 * Math.PI)) * 360 + frame * 0.5) % 360;
      const gold = interpolate(random(`g-${i}`), [0, 1], [30, 55]);
      hue = interpolate(trsP, [0, 1], [rainbow, gold]);
    }
    const sat = frame < P.trs.s ? 100 : interpolate(trsP, [0, 1], [100, 80]);
    const lit = frame < P.trs.s ? 60 : interpolate(trsP, [0, 1], [60, 70]);

    return {
      sx: cx + pos.x * birthF,
      sy: cy + pos.y * birthF,
      z: pos.z,
      size: p.size * sizeK * birthF,
      opacity,
      hue,
      sat,
      lit,
      alive: frame >= birthFrame,
    };
  });

  const sorted = [...particleData]
    .filter((p) => p.alive)
    .sort((a, b) => a.z - b.z);

  const showConn = frame >= P.frm.s;
  const connAlpha = showConn
    ? ci(frame, [P.frm.s, P.frm.s + D * 0.06], [0, 0.4])
    : 0;

  return (
    <AbsoluteFill
      style={{
        background:
          'radial-gradient(ellipse at 50% 50%, #0a0a2e 0%, #000008 100%)',
      }}
    >
      {/* 暗角 */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background:
            'radial-gradient(ellipse at 50% 50%, transparent 40%, rgba(0,0,0,0.6) 100%)',
          pointerEvents: 'none',
        }}
      />

      {/* 核心发光 */}
      <div
        style={{
          position: 'absolute',
          left: cx - 200,
          top: cy - 200,
          width: 400,
          height: 400,
          borderRadius: '50%',
          background: `radial-gradient(circle,
            hsla(${baseHue},100%,70%,${coreGlow * 0.4}) 0%,
            hsla(${baseHue},100%,50%,${coreGlow * 0.15}) 40%,
            transparent 70%)`,
          filter: `blur(${20 + coreGlow * 30}px)`,
          pointerEvents: 'none',
        }}
      />

      {/* Canvas：粒子 + 连线 */}
      <canvas
        ref={(canvas) => {
          if (!canvas) return;
          const ctx = canvas.getContext('2d');
          if (!ctx) return;
          canvas.width = width;
          canvas.height = height;
          ctx.clearRect(0, 0, width, height);

          if (showConn) {
            let n = 0;
            for (
              let i = 0;
              i < sorted.length && n < CONFIG.maxConnections;
              i++
            ) {
              for (
                let j = i + 1;
                j < sorted.length && n < CONFIG.maxConnections;
                j++
              ) {
                const dx = sorted[i].sx - sorted[j].sx;
                const dy = sorted[i].sy - sorted[j].sy;
                const d = Math.sqrt(dx * dx + dy * dy);
                if (d < CONFIG.connectionDist) {
                  const a =
                    (1 - d / CONFIG.connectionDist) *
                    connAlpha *
                    Math.min(sorted[i].opacity, sorted[j].opacity);
                  const h = (sorted[i].hue + sorted[j].hue) / 2;
                  ctx.beginPath();
                  ctx.moveTo(sorted[i].sx, sorted[i].sy);
                  ctx.lineTo(sorted[j].sx, sorted[j].sy);
                  ctx.strokeStyle = `hsla(${h},80%,60%,${a})`;
                  ctx.lineWidth = 0.6;
                  ctx.stroke();
                  n++;
                }
              }
            }
          }

          sorted.forEach((p) => {
            const g = ctx.createRadialGradient(
              p.sx,
              p.sy,
              0,
              p.sx,
              p.sy,
              p.size * 3,
            );
            g.addColorStop(
              0,
              `hsla(${p.hue},${p.sat}%,${p.lit}%,${p.opacity})`,
            );
            g.addColorStop(
              0.5,
              `hsla(${p.hue},${p.sat}%,${p.lit}%,${p.opacity * 0.3})`,
            );
            g.addColorStop(
              1,
              `hsla(${p.hue},${p.sat}%,${p.lit}%,0)`,
            );
            ctx.beginPath();
            ctx.arc(p.sx, p.sy, p.size * 3, 0, Math.PI * 2);
            ctx.fillStyle = g;
            ctx.fill();

            ctx.beginPath();
            ctx.arc(p.sx, p.sy, p.size, 0, Math.PI * 2);
            ctx.fillStyle = `hsla(${p.hue},${p.sat}%,${Math.min(p.lit + 20, 90)}%,${p.opacity})`;
            ctx.fill();
          });
        }}
        style={{ position: 'absolute', width, height }}
      />

      {/* 扫描线 */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background: `repeating-linear-gradient(0deg,transparent,transparent ${CONFIG.scanlineGap}px,rgba(255,255,255,${CONFIG.scanlineAlpha}) ${CONFIG.scanlineGap}px,rgba(255,255,255,${CONFIG.scanlineAlpha}) ${CONFIG.scanlineGap + 1}px)`,
          pointerEvents: 'none',
          opacity: frame < P.frm.s ? 0.8 : 0.35,
        }}
      />

      {/* 全息微光 */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background: `radial-gradient(circle at 50% 50%,hsla(${(frame * 3) % 360},100%,50%,0.03) 0%,transparent 60%)`,
          pointerEvents: 'none',
          opacity: Math.sin(frame * 0.15) * 0.5 + 0.5,
        }}
      />

      {/* ====== 第一幕：觉醒 —— 启动序列 ====== */}
      {frame < P.frm.e && (
        <div
          style={{
            position: 'absolute',
            top: 70,
            left: 0,
            right: 0,
            textAlign: 'center',
            opacity:
              frame < D * 0.01
                ? 0
                : frame < P.frm.s
                  ? 1
                  : ci(frame, [P.frm.s, P.frm.s + D * 0.04], [1, 0]),
          }}
        >
          <div
            style={{
              fontFamily: '"SF Mono","Fira Code",monospace',
              fontSize: 26,
              color: 'rgba(0,212,255,0.8)',
              letterSpacing: 4,
              textShadow: '0 0 10px rgba(0,212,255,0.5)',
            }}
          >
            {frame < P.awk.e * 0.65
              ? `QUANTUM CORE INITIALIZING${'...'.slice(0, (Math.floor(frame / 8) % 3) + 1)}`
              : 'QUANTUM CORE ONLINE ✓'}
          </div>
          <div
            style={{
              fontFamily: '"SF Mono","Fira Code",monospace',
              fontSize: 24,
              color: 'rgba(0,212,255,0.45)',
              marginTop: 10,
              letterSpacing: 2,
            }}
          >
            PARTICLES:{' '}
            {Math.min(
              Math.floor(awkP * CONFIG.particleCount),
              CONFIG.particleCount,
            )}{' '}
            / {CONFIG.particleCount}
          </div>
          {/* 进度条 */}
          <div
            style={{
              margin: '14px auto 0',
              width: 240,
              height: 2,
              background: 'rgba(0,212,255,0.15)',
              borderRadius: 1,
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                width: `${awkP * 100}%`,
                height: '100%',
                background:
                  'linear-gradient(90deg,#00d4ff,#a855f7)',
                borderRadius: 1,
                boxShadow: '0 0 8px rgba(0,212,255,0.6)',
              }}
            />
          </div>
        </div>
      )}

      {/* ====== 第二幕：成形 —— 主标题 ====== */}
      {frame >= P.frm.s - D * 0.02 && frame < P.cap.e && (
        <div
          style={{
            position: 'absolute',
            top: 55,
            left: 0,
            right: 0,
            textAlign: 'center',
            opacity:
              ci(frame, [P.frm.s, P.frm.s + D * 0.06], [0, 1]) *
              (frame >= P.cap.s
                ? ci(
                    frame,
                    [P.cap.e - D * 0.06, P.cap.e],
                    [1, 0],
                  )
                : 1),
            transform: `translateY(${ci(frame, [P.frm.s, P.frm.s + D * 0.06], [30, 0])}px)`,
          }}
        >
          <div
            style={{
              fontFamily: 'system-ui,-apple-system,sans-serif',
              fontSize: 54,
              fontWeight: 800,
              background:
                'linear-gradient(135deg,#00d4ff 0%,#a855f7 50%,#00d4ff 100%)',
              backgroundSize: '200% auto',
              backgroundPosition: `${frame * 2}% center`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              letterSpacing: 8,
              filter:
                'drop-shadow(0 0 20px rgba(0,212,255,0.35))',
            }}
          >
            全息量子引擎
          </div>
          <div
            style={{
              fontFamily: '"SF Mono","Fira Code",monospace',
              fontSize: 20,
              color: 'rgba(168,85,247,0.65)',
              marginTop: 14,
              letterSpacing: 10,
              opacity: ci(
                frame,
                [P.frm.s + D * 0.04, P.frm.s + D * 0.1],
                [0, 1],
              ),
            }}
          >
            HOLOGRAPHIC QUANTUM ENGINE
          </div>
        </div>
      )}

      {/* ====== 第三幕：赋能 —— 能力卡片 ====== */}
      {frame >= P.cap.s - D * 0.01 && frame < P.trs.s + D * 0.04 && (
        <>
          {FEATURES.map((feat, idx) => {
            const cardStart = P.cap.s + feat.pct * D;
            const cardFade = P.trs.s;
            const isLeft = idx % 2 === 0;

            const cardOpacity =
              ci(frame, [cardStart, cardStart + D * 0.04], [0, 1]) *
              ci(frame, [cardFade, cardFade + D * 0.04], [1, 0]);
            const slideX = ci(
              frame,
              [cardStart, cardStart + D * 0.05],
              [isLeft ? -60 : 60, 0],
            );

            const cardY = 340 + idx * 120;
            const cardX = isLeft ? 110 : width - 480;

            return (
              <div
                key={idx}
                style={{
                  position: 'absolute',
                  left: cardX,
                  top: cardY,
                  width: 370,
                  opacity: cardOpacity,
                  transform: `translateX(${slideX}px)`,
                }}
              >
                <div
                  style={{
                    background: 'rgba(255,255,255,0.04)',
                    borderRadius: 16,
                    padding: '20px 26px',
                    border: `1px solid ${feat.color}33`,
                    backdropFilter: 'blur(12px)',
                    boxShadow: `0 0 24px ${feat.color}18, inset 0 0 24px ${feat.color}0a`,
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 14,
                    }}
                  >
                    <span
                      style={{
                        fontSize: 40,
                        color: feat.color,
                        filter: `drop-shadow(0 0 6px ${feat.color})`,
                      }}
                    >
                      {feat.icon}
                    </span>
                    <div>
                      <div
                        style={{
                          fontFamily: 'system-ui,sans-serif',
                          fontSize: 26,
                          fontWeight: 700,
                          color: 'rgba(255,255,255,0.9)',
                          letterSpacing: 2,
                        }}
                      >
                        {feat.title}
                      </div>
                      <div
                        style={{
                          fontFamily: '"SF Mono",monospace',
                          fontSize: 24,
                          color: feat.color,
                          marginTop: 6,
                          letterSpacing: 1,
                        }}
                      >
                        <span
                          style={{
                            fontSize: 24,
                            fontWeight: 700,
                          }}
                        >
                          {feat.stat}
                        </span>
                        <span
                          style={{
                            marginLeft: 10,
                            opacity: 0.7,
                          }}
                        >
                          {feat.label}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </>
      )}

      {/* ====== 第四幕：超越 —— 重新定义 ====== */}
      {frame >= P.trs.s - D * 0.01 && frame < P.lch.s + D * 0.02 && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: 'none',
          }}
        >
          {/* 金色光环 */}
          <div
            style={{
              position: 'absolute',
              width: radius * 2 + 120,
              height: radius * 2 + 120,
              borderRadius: '50%',
              border: '2px solid rgba(255,215,0,0.25)',
              opacity:
                ci(
                  frame,
                  [P.trs.s + D * 0.04, P.trs.s + D * 0.08],
                  [0, 0.6],
                ) *
                ci(
                  frame,
                  [P.lch.s - D * 0.02, P.lch.s],
                  [1, 0],
                ),
              boxShadow:
                '0 0 40px rgba(255,215,0,0.2), inset 0 0 40px rgba(255,215,0,0.1)',
            }}
          />

          <div
            style={{
              opacity:
                ci(
                  frame,
                  [P.trs.s + D * 0.02, P.trs.s + D * 0.08],
                  [0, 1],
                ) *
                ci(
                  frame,
                  [P.lch.s - D * 0.03, P.lch.s],
                  [1, 0],
                ),
              transform: `scale(${ci(frame, [P.trs.s + D * 0.02, P.trs.s + D * 0.1], [0.8, 1])})`,
              textAlign: 'center',
            }}
          >
            <div
              style={{
                fontFamily: 'system-ui,sans-serif',
                fontSize: 58,
                fontWeight: 800,
                color: 'rgba(255,255,255,0.95)',
                letterSpacing: 14,
                textShadow:
                  '0 0 30px rgba(255,215,0,0.6), 0 0 60px rgba(255,215,0,0.3)',
                lineHeight: 1.5,
              }}
            >
              重新定义
            </div>
            <div
              style={{
                fontFamily: 'system-ui,sans-serif',
                fontSize: 58,
                fontWeight: 800,
                color: 'rgba(255,215,0,0.9)',
                letterSpacing: 14,
                textShadow:
                  '0 0 30px rgba(255,215,0,0.6), 0 0 60px rgba(255,215,0,0.3)',
                lineHeight: 1.5,
                opacity: ci(
                  frame,
                  [P.trs.s + D * 0.06, P.trs.s + D * 0.13],
                  [0, 1],
                ),
              }}
            >
              人机交互
            </div>
          </div>
        </div>
      )}

      {/* ====== 第五幕：发布 —— 品牌亮相 ====== */}
      {frame >= P.lch.s - D * 0.01 && (
        <>
          {/* 白色闪光 */}
          <div
            style={{
              position: 'absolute',
              inset: 0,
              background: 'white',
              opacity: ci3(
                frame,
                [P.lch.s, P.lch.s + D * 0.01, P.lch.s + D * 0.03],
                [0, 0.7, 0],
              ),
              pointerEvents: 'none',
            }}
          />

          <div
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              opacity: ci(
                frame,
                [P.lch.s + D * 0.02, P.lch.s + D * 0.06],
                [0, 1],
              ),
              transform: `translateY(${ci(frame, [P.lch.s + D * 0.02, P.lch.s + D * 0.07], [20, 0])}px)`,
              pointerEvents: 'none',
            }}
          >
            <div
              style={{
                fontFamily: '"SF Mono","Fira Code",monospace',
                fontSize: 68,
                fontWeight: 700,
                color: 'rgba(255,255,255,0.95)',
                letterSpacing: 18,
                textShadow:
                  '0 0 20px rgba(255,215,0,0.4), 0 0 40px rgba(255,215,0,0.2)',
              }}
            >
              HOLOGRAPHIC
            </div>
            <div
              style={{
                fontFamily: '"SF Mono",monospace',
                fontSize: 40,
                color: 'rgba(255,215,0,0.8)',
                letterSpacing: 22,
                marginTop: 6,
              }}
            >
              2 0 2 6
            </div>

            {/* 分隔线 */}
            <div
              style={{
                width: ci(
                  frame,
                  [P.lch.s + D * 0.04, P.lch.s + D * 0.09],
                  [0, 180],
                ),
                height: 1,
                background:
                  'linear-gradient(90deg,transparent,rgba(255,215,0,0.5),transparent)',
                marginTop: 28,
              }}
            />

            <div
              style={{
                fontFamily: 'system-ui,sans-serif',
                fontSize: 24,
                color: 'rgba(255,255,255,0.5)',
                letterSpacing: 6,
                marginTop: 20,
                opacity: ci(
                  frame,
                  [P.lch.s + D * 0.05, P.lch.s + D * 0.1],
                  [0, 1],
                ),
              }}
            >
              未来已来 · THE FUTURE IS NOW
            </div>
          </div>
        </>
      )}
    </AbsoluteFill>
  );
};

// ============ Composition 包装 ============
export const HolographicParticlesComposition = () => (
  <HolographicParticles />
);
